import { Box, Chip, Divider, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { type FC, useMemo } from 'react';

import { type VisionStageView } from '../../api/index.js';

interface Props {
  frame: (VisionStageView & { frameIndex: number }) | null;
}

const KEYPOINT_COUNT = 17;

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

const VisionStageReadout: FC<Props> = ({ frame }) => {
  const labelCounts = useMemo(() => {
    const counts = new Map<string, number>();
    frame?.objects.forEach((item) => counts.set(item.label, (counts.get(item.label) ?? 0) + 1));
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [frame]);

  if (!frame) {
    return (
      <Typography variant="body2" color="text.secondary">
        Waiting for the first frame…
      </Typography>
    );
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="caption" color="text.secondary">
        Frame {frame.frameIndex} · {frame.width}×{frame.height} · {frame.lighting === 'ir' ? 'night (IR)' : 'day'} ·
        {' '}processing {frame.latencyMs} ms
      </Typography>

      <Divider />
      <Typography variant="overline" color="text.secondary">What it sees</Typography>
      {labelCounts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Nothing detected at this stage.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {labelCounts.map(([label, count]) => <Chip key={label} size="small" label={`${label} × ${count}`} />)}
        </Box>
      )}
      {frame.objects.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Object</TableCell>
              <TableCell align="right">Confidence</TableCell>
              <TableCell align="right">Track</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {frame.objects.map((item, index) => (
              <TableRow key={`${item.label}-${item.trackId ?? index}-${index}`}>
                <TableCell>{item.label}</TableCell>
                <TableCell align="right">{formatPercent(item.confidence)}</TableCell>
                <TableCell align="right">{item.trackId === null ? '—' : `#${item.trackId}`}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {frame.people.length > 0 && (
        <>
          <Divider />
          <Typography variant="overline" color="text.secondary">How the pipeline reads each person</Typography>
          {frame.people.map((person, index) => (
            <Stack key={person.trackId ?? `untracked-${index}`} direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 36 }}>
                {person.trackId === null ? '—' : `#${person.trackId}`}
              </Typography>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {person.posture}{person.zoneType ? ` in ${person.zoneType}` : ''}
                {' · '}{person.visibleKeypoints}/{KEYPOINT_COUNT} points
                {person.patientScore !== null ? ` · score ${person.patientScore.toFixed(2)}` : ''}
              </Typography>
              {person.trackId !== null && person.trackId === frame.patientTrackId && (
                <Chip size="small" color="success" label="patient" />
              )}
            </Stack>
          ))}
        </>
      )}

      <Divider />
      <Typography variant="body2" color="text.secondary">
        Motion boxes: {frame.motion.length}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Yellow outlines are raw motion (anything that moved, recognised or not). Green is the person picked as the
        patient. Blue outlines are the camera zones. Trails show where each tracked object moved.
      </Typography>
    </Stack>
  );
};

export default VisionStageReadout;
